// Web Bluetooth helper for weighing scales that expose the standard
// Bluetooth SIG "Weight Scale" GATT service (only works for BLE scales -
// classic Bluetooth SPP/RFCOMM serial scales are NOT reachable from a
// browser; see note in WeightSticker.jsx).

const WEIGHT_SCALE_SERVICE = 0x181d;
const WEIGHT_MEASUREMENT_CHARACTERISTIC = 0x2a9d;

export const isBluetoothSupported = () =>
  typeof navigator !== "undefined" && "bluetooth" in navigator;

// Decodes the Weight Measurement characteristic per the Bluetooth GATT spec:
// byte 0 = flags (bit0: 0=kg/0.005 resolution, 1=lb/0.01 resolution),
// bytes 1-2 = weight as a little-endian uint16.
function parseWeightMeasurement(dataView) {
  if (!dataView || dataView.byteLength < 3) return null;
  const flags = dataView.getUint8(0);
  const isImperial = (flags & 0x01) === 1;
  const raw = dataView.getUint16(1, true);
  return isImperial ? raw * 0.01 * 0.453592 : raw * 0.005;
}

// Opens the browser's native Bluetooth device chooser (shows nearby BLE
// devices), connects to the GATT server, and subscribes to weight updates.
// Returns { device, disconnect }.
export async function connectBluetoothScale({ onWeight, onError, showAllDevices = false } = {}) {
  if (!isBluetoothSupported()) {
    throw new Error(
      "Web Bluetooth is not supported here. Use Chrome/Edge on a PC with Bluetooth, over HTTPS (or localhost).",
    );
  }

  // By default only list devices actively advertising the standard BLE
  // "Weight Scale" service, so unrelated gadgets (phones, earbuds, etc.)
  // don't clutter the picker. Most low-cost scales use classic Bluetooth
  // (SPP/RFCOMM) instead of BLE, so they will never show up here at all -
  // those must be paired in the OS Bluetooth settings and connected via
  // the "USB/Serial" option above instead.
  const device = await navigator.bluetooth.requestDevice(
    showAllDevices
      ? { acceptAllDevices: true, optionalServices: [WEIGHT_SCALE_SERVICE] }
      : { filters: [{ services: [WEIGHT_SCALE_SERVICE] }] },
  );

  const server = await device.gatt.connect();
  let characteristic;
  try {
    const service = await server.getPrimaryService(WEIGHT_SCALE_SERVICE);
    characteristic = await service.getCharacteristic(
      WEIGHT_MEASUREMENT_CHARACTERISTIC,
    );
  } catch (err) {
    server.disconnect();
    throw new Error(
      `"${device.name || "This device"}" doesn't expose the standard Bluetooth Weight Scale service. ` +
        "Many industrial scales use classic Bluetooth (SPP) instead - pair it in Windows Bluetooth settings and connect via the Serial option instead.",
    );
  }

  const handleValueChanged = (event) => {
    const weightKg = parseWeightMeasurement(event.target.value);
    if (weightKg !== null) onWeight?.(weightKg);
  };
  const handleDisconnected = () => onError?.(new Error("Bluetooth scale disconnected"));

  characteristic.addEventListener("characteristicvaluechanged", handleValueChanged);
  device.addEventListener("gattserverdisconnected", handleDisconnected);
  await characteristic.startNotifications();

  return {
    device,
    disconnect: async () => {
      characteristic.removeEventListener(
        "characteristicvaluechanged",
        handleValueChanged,
      );
      device.removeEventListener("gattserverdisconnected", handleDisconnected);
      try {
        await characteristic.stopNotifications();
      } catch (e) {
        /* ignore */
      }
      try {
        device.gatt.disconnect();
      } catch (e) {
        /* ignore */
      }
    },
  };
}
